import { ReadonlyVec2, vec3 } from 'gl-matrix';
import { ImageObject } from '~/engine/data/image/ImageObject';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import {
    Orientations,
    PlaneView,
    PlaneViewType,
} from '~/engine/view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { ModifierKeys } from '../../ModifierKeys';
import { StaticBrushCursorAction } from '../../actions/contouring-actions/StaticBrushCursorAction';
import { StaticBrushDrawAction } from '../../actions/contouring-actions/StaticBrushDrawAction';
import { MOUSE_BUTTONS_NONE } from '../../MouseButton';
import { BrushTool } from './BrushTool';

/**
 * {@link StaticBrushTool} is a {@link BrushTool} that can be used to draw changes on contours.\
 * It can draw both planar circles as well as 3D spheres.
 */
export class StaticBrushTool extends BrushTool {
    /**
     * {@link ModifierKeys} that need to be pressed in order to change the size of the brush.
     */
    public static readonly BRUSH_SIZE_MODIFIER_KEYS = ModifierKeys.SHIFT;

    /**
     * {@link StaticBrushTool} constructor.
     */
    public constructor(
        private readonly image: ImageObject,
        protected readonly structure: RegionOfInterest,
        erase: boolean,
        brushDiameter: number,
        brushSizeChangedCallback: (brushSize: number) => void
    ) {
        super(erase, brushDiameter, structure, brushSizeChangedCallback);
    }

    /**
     * Method to create a {@link StaticBrushCursorAction} instance, which is the type of {@link CursorAction} used by {@link StaticBrushTool}.
     * @returns A new {@link StaticBrushCursorAction} instance, with diameter set to {@link StaticBrushTool.diameter}.
     */
    protected resetCursorAction(): void {
        if (this._cursorAction) {
            this._cursorAction.stopAction();
        }
        // Use a distinct red color when erasing so the user can clearly
        // distinguish erase mode from draw mode at a glance.
        const cursorColor = this._erase
            ? vec3.fromValues(1, 0, 0)
            : this.structure.color;
        this._cursorAction = new StaticBrushCursorAction(
            this.image,
            this._diameter,
            cursorColor
        );
    }

    protected resetDrawAction(): void {
        this._drawAction?.stopAction();
        this._drawAction = new StaticBrushDrawAction(
            this.structure,
            this.image,
            this._diameter,
            this._erase,
            0,
            1
        );
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        view: PlaneView
    ): EventHandlerResult {
        if (buttons === MOUSE_BUTTONS_NONE && this._drawState) {
            this._drawState.clearMemory();
            this._drawState = undefined;
        }
        if (this._drawState) {
            return this._drawState.onMouseMove(coordinates, buttons, view);
        }
        if (
            view instanceof PlaneView &&
            view.label === Orientations.TRANSVERSAL_ORIENTATION &&
            this.structure.canModify &&
            view.type === PlaneViewType.Primary
        ) {
            if (!this._cursorAction) {
                this.resetCursorAction();
            }
            return (
                this._cursorAction?.onMouseMove(coordinates, buttons, view) ??
                new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED)
            );
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }
}
